// The office's Stock page in Blueprint (truck stock, concept A, BL-203, approved 27 Oct): the Storybook
// page Pages/Accounting/Stock, assembled (packages/ui-web stories/StockPage.stories.tsx), drawn from
// ui-web's parts over the real actions. The places switch (StatusBar, stockLocations.list); the place's
// name and its two actions; its facts (Figures); its table on the left (StockTable over
// stock.levels, with the catalog's codes, units and categories from stock.catalog), or every place
// side by side; and on the right what came from its tech (WorkList): a count sent in (stockCounts.open
// → CountReview in place of the table → stockCounts.accept with its recounts and extras), the next
// refill (picklists.list/get → RefillLines → picklists.setLine for each trim or removal, then
// picklists.post), a restock received short or extra (picklists.get's issues → RestockReceived →
// restockIssues.decide), with what a vendor still owes the truck (purchaseOrders.list's back-orders →
// purchaseOrders.cancel), and a receipt to file (jobPurchases.list → ReceiptFiling →
// jobPurchases.approve with its lines' filing). Each working view stays as it ended (its done notice)
// until it's closed; then the column reads again. Our cost only for whoever sees it: the answers carry it.
// Kept from the old view: an item's sheet (its levels, min and max, vendor), renaming a place and adding
// one (the line under the table); moving stock and the picklists are their own tabs, as before.
import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  CountReview,
  Figures,
  LineTable,
  Link,
  ReceiptFiling,
  RefillLines,
  RestockReceived,
  StatusBar,
  stockLines,
  WorkList,
  type CountDecision,
  type ReceiptFilingDecision,
  type WorkListItem,
} from '@flowology/ui-web/fl';
import type { StockPlace } from '@flowology/ui-web';
import { RecordLayout } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { approveDefaults, approveInput, cardsOf } from '../books/receipts-model.ts';
import { errorText, Loading, Panel, useAct, useQuery } from '../books/ui.tsx';
import { holdsAny } from '../shared/may.ts';
import { newId } from '../shared/ids.ts';
import { StockLocationSheet } from '../settings/Setup.tsx';
import { ItemSheet } from './ItemSheet.tsx';
import { RenameLocation } from './RenameLocation.tsx';
import { orderLocations, type StockLocation } from './model.ts';
import type { OpenCount } from './truck-counts-model.ts';
import {
  acceptInput,
  addDays,
  backOrdersFor,
  clockWords,
  countSays,
  countView,
  dayWords,
  everyFacts,
  filingLines,
  findIn,
  firstName,
  itemRows,
  momentWords,
  placeFacts,
  placeTabs,
  refillChanges,
  refillLinesOf,
  refillName,
  refillSays,
  receiptSays,
  receiptView,
  restockDecisions,
  restockView,
  shortName,
  stockPlaces,
  supplierOf,
  toDecide,
  type BackOrder,
  type CatalogEntry,
  type LevelAnswer,
  type Picklist,
  type Receipt,
} from './stock-page-model.ts';
import './stock-page.css';

interface Props {
  held: readonly string[];
  today: string;
  timeZone?: string;
  toast: (m: string) => void;
  mayManage: boolean;
  mayOrder: boolean;
  /** The signed-in person's staff record: who posts a count. */
  staffId?: string | null;
  /** The place shown; null: every place. */
  focus: string | null;
  onFocus: (placeId: string | null) => void;
  /** Counts' tab: the warehouse is counted there. */
  onCounts: () => void;
}

/** A phone: the table and the count drawn narrow. */
function useNarrow(): boolean {
  const q = '(max-width: 719px)';
  const [narrow, setNarrow] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const m = matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}

/** Every part in the catalog (stock.catalog, a page at a time), by id: the codes, units and categories. */
function useCatalog(on: boolean): ReadonlyMap<string, CatalogEntry> | null {
  const [map, setMap] = useState<ReadonlyMap<string, CatalogEntry> | null>(on ? null : new Map());
  useEffect(() => {
    if (!on) return;
    let live = true;
    void (async () => {
      const out = new Map<string, CatalogEntry>();
      let after: string | null = null;
      try {
        for (let page = 0; page < 200; page++) {
          const r: { parts: CatalogEntry[]; next: string | null } = await client.call('stock.catalog', {
            includeInactive: true,
            limit: 100,
            ...(after ? { after } : {}),
          });
          for (const p of r.parts) out.set(p.id, p);
          if (!r.next) break;
          after = r.next;
        }
      } catch {
        // without the catalog the table draws stock.levels' names alone, in one group
      }
      if (live) setMap(out);
    })();
    return () => {
      live = false;
    };
  }, [on]);
  return map;
}

interface Work {
  count: OpenCount | null;
  refill: Picklist | null;
  restock: Picklist | null;
  backOrders: BackOrder[];
  receipts: Receipt[];
  lastRestock: { day: string; lines: number } | null;
  cards: { id: string; name: string }[];
}
async function quietly<T>(p: Promise<T>, fallback: T): Promise<T> {
  try {
    return await p;
  } catch {
    return fallback;
  }
}

/**
 * What came from a truck's tech, as the office reads it: the count sent in, the refill open for today or
 * later, the last restock received with something to decide, the back-orders owed it, the receipts
 * waiting. The picklists are read a day at a time (picklists.list), the last week's and tomorrow's.
 */
function useWork(place: StockLocation | null, p: Props, version: number): Work | null {
  // read again (a view closed), the last answer stays drawn until the new one comes; another place's never
  const [work, setWork] = useState<(Work & { placeId: string }) | null>(null);
  const { held, today, mayManage, mayOrder } = p;
  useEffect(() => {
    if (!place || place.kind !== 'truck') return;
    let live = true;
    void (async () => {
      const call = <T,>(action: string, input: unknown, fallback: T) =>
        quietly(client.call<T>(action, input), fallback);
      const days = Array.from({ length: 9 }, (_, i) => addDays(today, 1 - i));
      const [counts, lists, orders, vendors, receipts] = await Promise.all([
        mayManage
          ? call<{ counts: OpenCount[] }>('stockCounts.open', { locationId: place.id }, { counts: [] })
          : { counts: [] },
        Promise.all(
          days.map((date) =>
            call<{ picklists: { id: string; locationId: string }[] }>(
              'picklists.list',
              { date },
              { picklists: [] },
            ),
          ),
        ),
        mayOrder
          ? call<{ orders: Parameters<typeof backOrdersFor>[0][number][] }>(
              'purchaseOrders.list',
              { status: 'partially_received' },
              { orders: [] },
            )
          : { orders: [] },
        holdsAny(held, ['vendors.view'])
          ? call<{ vendors: { id: string; name: string }[] }>('vendors.list', {}, { vendors: [] })
          : { vendors: [] },
        holdsAny(held, ['ap.manage']) && place.staffId
          ? call<{ purchases: Receipt[] }>(
              'jobPurchases.list',
              { status: 'submitted', staffId: place.staffId },
              { purchases: [] },
            )
          : { purchases: [] },
      ]);
      const ids = lists.flatMap((l) => l.picklists.filter((x) => x.locationId === place.id).map((x) => x.id));
      const picklists = (
        await Promise.all(ids.map((id) => call<Picklist | null>('picklists.get', { id }, null)))
      ).filter((x): x is Picklist => !!x);
      const byDate = [...picklists].sort((a, b) => a.date.localeCompare(b.date));
      const done = byDate.filter((x) => x.status !== 'open' && x.date <= today);
      const last = done[done.length - 1];
      const cards =
        receipts.purchases.length && holdsAny(held, ['accounting.view'])
          ? await (async () => {
              const [banks, accounts] = await Promise.all([
                call<{ bankAccounts: { id: string; name: string; accountId: string }[] }>(
                  'bankAccounts.list',
                  {},
                  { bankAccounts: [] },
                ),
                call<{ accounts: { id: string; subtype: string }[] }>(
                  'accounts.list',
                  { includeInactive: false },
                  { accounts: [] },
                ),
              ]);
              return cardsOf(banks.bankAccounts, accounts.accounts);
            })()
          : [];
      if (!live) return;
      setWork({
        placeId: place.id,
        count: counts.counts.find((c) => c.status === 'submitted') ?? null,
        refill: byDate.find((x) => x.status === 'open' && x.date >= today) ?? null,
        restock: [...byDate].reverse().find(toDecide) ?? null,
        backOrders: backOrdersFor(
          orders.orders,
          place.id,
          new Map(vendors.vendors.map((v) => [v.id, v.name])),
        ),
        receipts: receipts.purchases,
        lastRestock: last ? { day: dayWords(last.date), lines: last.lines.length } : null,
        cards,
      });
    })();
    return () => {
      live = false;
    };
  }, [place?.id, place?.kind, place?.staffId, today, mayManage, mayOrder, version]);
  return work && place && work.placeId === place.id ? work : null;
}

export function StockPage(p: Props) {
  const { held, toast, mayManage, mayOrder, focus, onFocus, timeZone } = p;
  const narrow = useNarrow();
  const seesCost = held.includes('pricebook.cost.view');
  const locs = useQuery<{ locations: StockLocation[] }>('stockLocations.list', {});
  const levels = useQuery<{ levels: LevelAnswer[] }>('stock.levels', {});
  const staff = useQuery<{ staff: { id: string; displayName: string }[] }>(
    'staff.list',
    holdsAny(held, ['staff.view']) ? { includeInactive: true } : null,
  );
  const catalog = useCatalog(holdsAny(held, ['pricebook.view']));
  const ordered = locs.data ? orderLocations(locs.data.locations) : null;
  const location = focus ? (ordered?.find((l) => l.id === focus) ?? null) : null;
  // what came from the tech reads again when a working view closes, so each keeps its done notice till then
  const [version, setVersion] = useState(0);
  const work = useWork(location, p, version);
  const [open, setOpen] = useState<string | null>(null);
  // a working view refused by the server starts again from what it reads
  const [tries, setTries] = useState(0);
  const [item, setItem] = useState<{ id: string; name: string } | null>(null);
  const [renaming, setRenaming] = useState<StockLocation | null>(null);
  const [adding, setAdding] = useState(false);
  const act = useAct(toast, async () => {
    await levels.reload();
  });
  useEffect(() => setOpen(null), [focus]);

  if (!ordered || !levels.data || !catalog)
    return (
      <Panel title="On hand">
        <Loading
          error={locs.error ?? levels.error}
          what="the stock"
          kind="table"
          cols={['Item', 'On hand', 'Min', 'Max']}
          retry={() => void Promise.all([locs.reload(), levels.reload()])}
        />
      </Panel>
    );
  const names = new Map((staff.data?.staff ?? []).map((s) => [s.id, s.displayName]));
  const places = stockPlaces(ordered, names);
  const place: StockPlace | null = location ? (places.find((x) => x.id === location.id) ?? null) : null;
  const all = levels.data.levels;
  const here = place ? all.filter((l) => l.locationId === place.id) : [];
  const rows = itemRows(all, place ? [place.id] : places.map((x) => x.id), catalog);
  const tech = location?.staffId ? names.get(location.staffId) : undefined;
  const first = tech ? firstName(tech) : 'the tech';
  const me = p.staffId ? names.get(p.staffId) : undefined;
  const reload = () => {
    setVersion((v) => v + 1);
    void levels.reload();
  };
  const refused = (e: unknown) => {
    toast(errorText(e));
    setTries((t) => t + 1);
    reload();
  };

  // ── what came from the tech, each with its working view ──
  const count = work?.count ? countView(work.count, here, catalog, all) : null;
  const counting = !!count && open === 'count';
  const items: WorkListItem[] = [];
  if (work && place) {
    if (work.count && count)
      items.push({
        id: 'count',
        title: `${first}’s count`,
        says: countSays(work.count, count, timeZone),
        action: 'Review and post',
        opens: 'in-place',
        body: (
          <p>
            The table beside shows what {first} counted against what {place.name} shows.
          </p>
        ),
      });
    if (work.refill) {
      const r = work.refill;
      const wh = places.find((x) => x.kind === 'warehouse');
      items.push({
        id: 'refill',
        title: `${refillName(r.date, p.today)}, ${dayWords(r.date)}`,
        says: refillSays(r),
        action: mayManage ? 'Check and send' : 'See it',
        opens: 'panel',
        panelTitle: `${refillName(r.date, p.today)}: ${dayWords(r.date)}`,
        panelSub: `For ${place.name}, from the warehouse`,
        wide: true,
        body: (
          <RefillLines
            key={`refill-${tries}`}
            refNo={`Refill ${dayWords(r.date)}`}
            at={dayWords(r.date)}
            first={first}
            lines={refillLinesOf(r, {
              first,
              warehouse: wh ? all.filter((l) => l.locationId === wh.id) : [],
              catalog,
              levels: all,
              ...(timeZone ? { timeZone } : {}),
            })}
            readOnly={!mayManage || r.status !== 'open'}
            onSend={(sent) => {
              void (async () => {
                try {
                  for (const change of refillChanges(r, sent)) await client.call('picklists.setLine', change);
                  await client.call('picklists.post', { picklistId: r.id, picked: [] });
                  void levels.reload();
                } catch (e) {
                  refused(e);
                }
              })();
            }}
          />
        ),
      });
    }
    if (work.restock || work.backOrders.length) {
      const view = restockView(work.restock, work.backOrders, {
        place: place.name,
        first,
        catalog,
        levels: all,
        seesCost,
        ...(timeZone ? { timeZone } : {}),
      });
      const r = work.restock;
      const ref = r ? `Restock ${dayWords(r.date)}` : 'The back-orders';
      items.push({
        id: 'restock',
        title: r ? `The restock for ${dayWords(r.date)} didn’t match` : `Back-ordered for ${place.name}`,
        says: view.says,
        action: 'Sort it out',
        opens: 'panel',
        panelTitle: r ? `The restock for ${dayWords(r.date)} didn’t match` : `Back-ordered for ${place.name}`,
        panelSub: r?.receivedAt
          ? `${place.name}, received ${momentWords(r.receivedAt, timeZone)}`
          : place.name,
        body: (
          <RestockReceived
            key={`restock-${tries}`}
            refNo={ref}
            head={view.head}
            {...(view.asSent ? { asSent: view.asSent } : {})}
            issues={view.issues}
            first={first}
            readOnly={!mayManage}
            onClose={(chosen) => {
              const d = restockDecisions(r, chosen);
              void (async () => {
                try {
                  if (d.decide) await client.call('restockIssues.decide', d.decide);
                  for (const poId of d.cancel)
                    await client.call('purchaseOrders.cancel', {
                      poId,
                      reason: `What was back-ordered for ${place.name} isn’t wanted any more.`,
                    });
                  void levels.reload();
                } catch (e) {
                  refused(e);
                }
              })();
            }}
          />
        ),
      });
    }
    for (const rc of work.receipts) {
      const paid = rc.vendorName
        ? `On ${rc.vendorName}’s account`
        : work.cards[0]
          ? `Company card: ${work.cards[0].name}`
          : 'Not said';
      const view = receiptView(rc, { place, catalog, truckLevels: here, seesCost, paid });
      if (!view) continue;
      const by = rc.staffName ? shortName(rc.staffName) : first;
      const slip =
        rc.receipt?.url && rc.receipt.kind !== 'document'
          ? {
              id: rc.receipt.fileId,
              src: rc.receipt.url,
              caption: `${supplierOf(rc)}’s slip`,
              by: `${by} · ${dayWords(rc.purchasedOn)}`,
              where: `Receipt ${rc.number}`,
              officeOnly: true,
            }
          : undefined;
      items.push({
        id: `receipt-${rc.id}`,
        title: `Receipt ${rc.number}, ${supplierOf(rc)}`,
        says: receiptSays(rc),
        action: 'File it',
        opens: 'panel',
        wide: true,
        panelTitle: `Receipt ${rc.number}`,
        panelSub: `${supplierOf(rc)}, ${dayWords(rc.purchasedOn)}, by ${by}`,
        body: (
          <ReceiptFiling
            key={`receipt-${rc.id}-${tries}`}
            id={rc.number}
            supplier={supplierOf(rc)}
            at={dayWords(rc.purchasedOn)}
            by={by}
            first={first}
            paid={paid}
            place={place.name}
            {...(slip ? { slip } : {})}
            {...view}
            find={(words) => findIn(catalog, words)}
            narrow
            onFile={(filing: ReceiptFilingDecision) => {
              const form = approveDefaults(rc, work.cards);
              void (async () => {
                try {
                  await client.call('jobPurchases.approve', {
                    ...approveInput(rc, form),
                    lines: filingLines(rc, filing, place.id),
                  });
                  void levels.reload();
                } catch (e) {
                  refused(e);
                }
              })();
            }}
          />
        ),
      });
    }
  }

  // ── the left column: the table, or his count beside what the books show ──
  const left: ReactNode = !place ? (
    <LineTable
      {...stockLines({
        places,
        rows,
        onOpenItem: (id) => setItem({ id, name: rows.find((r) => r.itemId === id)?.itemName ?? '' }),
        onOpenPlace: (id) => onFocus(id),
        empty: places.length
          ? 'Nothing is stocked yet. Receiving a purchase order puts stock in the warehouse.'
          : 'No warehouse or trucks yet.',
      })}
    />
  ) : counting && work?.count && count ? (
    <CountReview
      key={`count-${work.count.id}-${tries}`}
      place={place.name}
      tech={tech ? shortName(tech) : place.name}
      first={first}
      sentAt={work.count.submittedAt ? clockWords(work.count.submittedAt, timeZone) : ''}
      lines={count.lines}
      extra={count.extra}
      postedBy={me ? shortName(me) : 'The office'}
      onNotNow={() => setOpen(null)}
      narrow={narrow}
      onPost={(d: CountDecision) => {
        void act
          .run('stockCounts.accept', acceptInput(work.count!.id, d, count))
          .then((r) => r === null && setTries((t) => t + 1));
      }}
    />
  ) : (
    <LineTable
      {...stockLines({
        places: [place],
        rows,
        onOpenItem: (id) => setItem({ id, name: rows.find((r) => r.itemId === id)?.itemName ?? '' }),
        narrow,
        empty: !rows.length
          ? `Nothing ${place.kind === 'truck' ? 'on' : 'in'} ${place.name} yet. Set what it carries, and the next refill fills it.`
          : undefined,
      })}
    />
  );
  const right: ReactNode =
    place && items.length ? (
      <WorkList
        key={`${place.id}-${version}`}
        heading={`From ${first}`}
        items={items}
        defaultOpen={open}
        onOpenChange={(id) => {
          setOpen(id);
          if (id === null) setVersion((v) => v + 1);
        }}
      />
    ) : (
      <p className="stock-page__note">
        {!place
          ? 'Choose a place to see what came from its tech.'
          : place.kind === 'warehouse'
            ? 'The warehouse’s counts and orders are on their own tabs.'
            : !work
              ? 'Reading what came from its tech…'
              : 'Nothing from its tech today.'}
      </p>
    );
  const countIt =
    place && mayManage
      ? () => {
          if (place.kind === 'truck' && location?.staffId)
            void act.run(
              'stockCounts.request',
              { countId: newId(), locationId: place.id },
              `Asked: ${place.name}’s technician counts it on their iPad.`,
            );
          else p.onCounts();
        }
      : null;

  return (
    <div className="stock-page">
      <div className="stock-page__places">
        <StatusBar
          label="Places"
          groups={[placeTabs(places, all, place?.id ?? 'every')]}
          selected={place?.id ?? 'every'}
          onChange={(id) => onFocus(id === 'every' ? null : id)}
          oneRow={narrow}
        />
      </div>
      {focus && !place ? (
        <p className="stock-page__note">
          That truck or warehouse isn’t kept any more. <Link onPress={() => onFocus(null)}>Every place</Link>
        </p>
      ) : null}
      {place ? (
        <div className="stock-page__place">
          <h2>{place.name}</h2>
          {rows.length && !counting && countIt ? (
            <div className="stock-page__acts">
              <Button variant={items.length ? 'secondary' : 'primary'} onPress={countIt}>
                Count {place.name}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {place && !rows.length ? null : (
        <div className="stock-page__facts">
          <Figures
            label={place ? `${place.name} today` : 'Every place today'}
            items={
              place
                ? placeFacts(place, all, {
                    refillDay: work?.refill ? dayWords(work.refill.date) : null,
                    lastRestock: work?.lastRestock ?? null,
                  })
                : everyFacts(
                    all,
                    places.map((x) => x.id),
                  )
            }
          />
        </div>
      )}
      <RecordLayout
        main={left}
        side={right}
        sideLabel={place && items.length ? `From ${first}` : 'From the techs'}
      />
      {mayManage ? (
        <p className="stock-page__more">
          {location ? (
            <>
              <Link onPress={() => setRenaming(location)}>Rename {location.name}</Link>
              {' · '}
            </>
          ) : null}
          <Link onPress={() => setAdding(true)}>Add a truck or warehouse</Link>
        </p>
      ) : null}
      {item && (
        <ItemSheet
          itemId={item.id}
          itemName={item.name}
          locations={ordered}
          mayManage={mayManage}
          mayVendors={mayOrder}
          onClose={() => setItem(null)}
          onChanged={(said) => {
            toast(said);
            void levels.reload();
          }}
        />
      )}
      {renaming && (
        <RenameLocation
          location={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={(name) => {
            toast(`${renaming.name} is now ${name}.`);
            setRenaming(null);
            void locs.reload();
          }}
        />
      )}
      {adding && (
        <StockLocationSheet
          onSaved={() => {
            setAdding(false);
            toast('Added. It shows here once it has stock.');
            void locs.reload();
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}
